import { useCallback, useRef } from 'react'
import type { OpusWebCodecs, ZelloState } from './types'
import { buildZelloAudioPacket, opusCodecHeaderB64 } from './protocol'
import { fireError, fireWarning } from 'models/PopUpNotifications'
import { getSharedAudioContext, unlockPcmAudio } from 'lib/audio/OpusStreamPlayer'

const NOOP = (_?: unknown) => { /* intentionally empty */ }
const DEBUG_AUDIO = true
const txlog = (...a: any[]) => { if (DEBUG_AUDIO) console.log('[TX]', ...a) }

async function createWebCodecsOpusEncoder(args: {
  sampleRate: number
  numberOfChannels: number
  bitrate?: number
  onOpus: (opusFrame: Uint8Array) => void
}): Promise<OpusWebCodecs> {
  const { sampleRate, numberOfChannels, bitrate = 24000, onOpus } = args

  const AnyAudioEncoder = (window as any).AudioEncoder
  if (!AnyAudioEncoder) throw new Error('WebCodecs AudioEncoder not available in this browser')

  const encoder = new AnyAudioEncoder({
    output: async (chunk: any) => {
      try {
        const ab = new ArrayBuffer(chunk.byteLength)
        chunk.copyTo(new Uint8Array(ab))
        if (DEBUG_AUDIO) console.log('[TX] encoder output', { bytes: chunk.byteLength, type: chunk.type, ts: chunk.timestamp })
        onOpus(new Uint8Array(ab))
      } catch (err) {
        NOOP(err)
      }
    },
    error: (e: any) => {
      console.error('[TX] AudioEncoder error', e)
    },
  })

  encoder.configure({
    codec: 'opus',
    sampleRate,
    numberOfChannels,
    bitrate,
    latencyMode: 'realtime',
  })

  const close = async () => {
    try { await encoder.flush() } catch (err) { NOOP(err) }
    try { encoder.close() } catch (err) { NOOP(err) }
  }

  return { encoder, close }
}

type UseZelloTxArgs = {
  authToken?: string
  seqRef: React.MutableRefObject<number>
  wsRef: React.MutableRefObject<WebSocket | null>
  isLoggedOnRef: React.MutableRefObject<boolean>
  activeTxChannelRef: React.MutableRefObject<string | null>
  safeSend: (obj: any) => boolean
  safeSendBinary: (buf: ArrayBuffer) => boolean
  setState: React.Dispatch<React.SetStateAction<ZelloState>>
  waitForChannelReady: (channel: string, timeoutMs?: number) => Promise<void>
  forceReconnect: () => Promise<void>
}

export function useZelloTx({
  authToken,
  seqRef,
  wsRef,
  isLoggedOnRef,
  activeTxChannelRef,
  safeSend,
  safeSendBinary,
  setState,
  waitForChannelReady,
  forceReconnect,
}: UseZelloTxArgs) {
  const txStreamIdRef = useRef<number | null>(null)
  const txChannelRef = useRef<string | null>(null)

  const txCtxRef = useRef<AudioContext | null>(null)
  const txProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const txSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const txMediaStreamRef = useRef<MediaStream | null>(null)
  const txZeroGainRef = useRef<GainNode | null>(null)

  const txWebCodecsRef = useRef<OpusWebCodecs | null>(null)
  const txTimestampUsRef = useRef(0)
  const startGenRef = useRef(0)

  const mediaErrorMessage = (err: any): string => {
    const name = String(err?.name || '')
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return 'Microphone permission was denied. Enable it in your browser settings and try again.'
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return 'No microphone was detected on this device.'
    }
    if (name === 'NotReadableError') {
      return 'Microphone is in use by another application.'
    }
    return String(err?.message || err || 'Microphone could not be started.')
  }

  const startTalking = useCallback(async (channelName?: string) => {
    const channel = (channelName || activeTxChannelRef.current || '').trim()
    if (!channel) {
      setState(p => ({ ...p, tx: 'idle', error: 'No TX channel selected', lastEvent: 'no_channel' }))
      fireWarning({ content: 'No channel selected to talk on.' })
      return
    }
    if (!isLoggedOnRef.current) {
      setState(p => ({ ...p, tx: 'idle', error: 'Not connected', lastEvent: 'not_logged_on' }))
      fireWarning({ content: 'Not connected to the channel server. Please wait and try again.' })
      return
    }
    if (txStreamIdRef.current != null) return

    // CRITICAL: kick off audio unlock synchronously inside the user-gesture call stack
    // so the shared AudioContext (used for both TX and RX) can transition to 'running'.
    // We don't await it here — we just want resume() initiated under the gesture.
    void unlockPcmAudio()

    const gen = ++startGenRef.current
    const isStale = () => startGenRef.current !== gen

    txlog('startTalking', { channel })
    setState(p => ({ ...p, tx: 'sending', error: undefined }))

    const sampleRate = 48000
    const numberOfChannels = 1
    const frameSizeMs = 20
    const framesPerPacket = 1

    const codec_header = opusCodecHeaderB64(sampleRate, framesPerPacket, frameSizeMs)

    const attemptStartStream = (): Promise<number> => new Promise<number>((resolve, reject) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return reject(new Error('ws not open'))

      const seq = seqRef.current++
      const startCmd: any = {
        command: 'start_stream',
        seq,
        channel,
        type: 'audio',
        codec: 'opus',
        packet_duration: frameSizeMs,
        codec_header,
      }
      if (authToken) startCmd.auth_token = authToken

      const onMsg = (ev: MessageEvent) => {
        if (typeof ev.data !== 'string') return
        let msg: any
        try { msg = JSON.parse(ev.data) } catch { return }
        if (msg?.seq !== seq) return

        ws.removeEventListener('message', onMsg as any)

        if (msg?.success && typeof msg?.stream_id === 'number') {
          resolve(Number(msg.stream_id))
        } else {
          reject(new Error(String(msg?.error || 'start_stream failed')))
        }
      }

      ws.addEventListener('message', onMsg as any)
      safeSend(startCmd)

      setTimeout(() => {
        try { ws.removeEventListener('message', onMsg as any) } catch (err) { NOOP(err) }
        reject(new Error('start_stream timeout'))
      }, 4000)
    })

    let streamId: number | null = null

    try {
      txlog('start_stream attempt 1')
      streamId = await attemptStartStream()
      txlog('start_stream attempt 1 ok', { streamId })
    } catch (err: any) {
      if (isStale()) return
      const errTxt = String(err?.message || err || 'start_stream failed').toLowerCase()
      const isNotReady = errTxt.includes('not ready')
      const isBusy = errTxt.includes('busy')

      if (isBusy) {
        setState(p => ({ ...p, tx: 'idle', error: errTxt, lastEvent: 'channel_busy' }))
        fireError({ content: 'Channel is busy. Try again shortly.' })
        return
      }
      if (!isNotReady) {
        setState(p => ({ ...p, tx: 'idle', error: errTxt, lastEvent: 'start_stream_error' }))
        fireError({ content: `Could not start talking: ${errTxt}` })
        return
      }

      // Channel-not-ready retry. Zello does NOT reliably emit
      // on_channel_status: online after logon — particularly after a quick
      // page refresh, where from the server's view the channel never
      // transitioned. So we cannot rely solely on the event. Race a backoff
      // timer against waitForChannelReady; whichever signals first triggers
      // the next start_stream attempt.
      txlog('channel not ready, retrying with backoff')
      setState(p => p.tx === 'sending' ? p : { ...p, tx: 'sending', error: undefined })

      const delays = [350, 600, 900, 1300, 1500] // ~4.65s total
      for (let i = 0; i < delays.length; i++) {
        if (isStale()) return

        await Promise.race([
          new Promise<void>(r => setTimeout(r, delays[i])),
          waitForChannelReady(channel, delays[i] + 100).catch(() => undefined),
        ])
        if (isStale()) return

        try {
          txlog(`start_stream retry ${i + 1}`)
          streamId = await attemptStartStream()
          txlog(`start_stream retry ${i + 1} ok`, { streamId })
          break
        } catch (err2: any) {
          if (isStale()) return
          const e2 = String(err2?.message || err2 || 'start_stream failed').toLowerCase()
          if (!e2.includes('not ready')) {
            setState(p => ({ ...p, tx: 'idle', error: e2, lastEvent: 'start_stream_error' }))
            fireError({ content: `Could not start talking: ${e2}` })
            return
          }
          // still not ready — fall through to next backoff step
        }
      }

      if (streamId == null) {
        // Last resort: rebuild the socket. Zello can leave a session stuck
        // where the channel never goes online and start_stream keeps failing,
        // and only a fresh logon clears it (equivalent to a page refresh).
        if (isStale()) return
        txlog('all retries failed, forcing socket reconnect')
        try {
          await forceReconnect()
        } catch (rcErr) {
          if (isStale()) return
          console.error('[TX] forceReconnect failed', rcErr)
          setState(p => ({ ...p, tx: 'idle', error: 'reconnect failed', lastEvent: 'reconnect_failed' }))
          fireError({ content: 'Lost connection to channel server. Please refresh the page.' })
          return
        }
        if (isStale()) return

        // forceReconnect's closeWs flipped tx to 'idle'; re-assert 'sending'
        // for the post-reconnect attempts so the UI stays consistent.
        setState(p => ({ ...p, tx: 'sending', error: undefined }))

        const postDelays = [400, 900, 1400] // ~2.7s
        for (let i = 0; i < postDelays.length; i++) {
          if (isStale()) return

          await Promise.race([
            new Promise<void>(r => setTimeout(r, postDelays[i])),
            waitForChannelReady(channel, postDelays[i] + 100).catch(() => undefined),
          ])
          if (isStale()) return

          try {
            txlog(`start_stream post-reconnect ${i + 1}`)
            streamId = await attemptStartStream()
            txlog(`start_stream post-reconnect ${i + 1} ok`, { streamId })
            break
          } catch (err3: any) {
            if (isStale()) return
            const e3 = String(err3?.message || err3 || 'start_stream failed').toLowerCase()
            if (!e3.includes('not ready')) {
              setState(p => ({ ...p, tx: 'idle', error: e3, lastEvent: 'start_stream_error' }))
              fireError({ content: `Could not start talking: ${e3}` })
              return
            }
          }
        }

        if (streamId == null) {
          if (isStale()) return
          setState(p => ({ ...p, tx: 'idle', error: 'channel not ready', lastEvent: 'channel_not_ready' }))
          fireError({ content: `Channel "${channel}" is not responding. Try rejoining the channel or refreshing the page.` })
          return
        }
      }
    }

    if (isStale()) {
      safeSend({ command: 'stop_stream', seq: seqRef.current++, stream_id: streamId, channel })
      return
    }

    let encoder: OpusWebCodecs | null = null
    let media: MediaStream | null = null
    let ctx: AudioContext | null = null
    let src: MediaStreamAudioSourceNode | null = null
    let proc: ScriptProcessorNode | null = null
    let zero: GainNode | null = null
    let ctxSampleRate = sampleRate

    try {
      ctx = getSharedAudioContext()
      if (!ctx) throw new Error('AudioContext is not supported in this browser')
      txlog('shared ctx acquired', { state: ctx.state, sampleRate: ctx.sampleRate })

      if (ctx.state !== 'running') {
        try { await ctx.resume() } catch (err) { NOOP(err) }
        txlog('ctx resumed', { state: ctx.state })
      }
      if (ctx.state !== 'running') {
        throw new Error('Audio is locked. Tap or click anywhere on the page first, then try again.')
      }
      if (isStale()) throw new Error('cancelled')

      ctxSampleRate = ctx.sampleRate

      encoder = await createWebCodecsOpusEncoder({
        sampleRate: ctxSampleRate,
        numberOfChannels,
        bitrate: 24000,
        onOpus: (opusFrame) => {
          const sid = txStreamIdRef.current
          if (sid == null) return
          const pkt = buildZelloAudioPacket(sid, opusFrame)
          safeSendBinary(pkt)
        },
      })
      txlog('encoder ready')
      if (isStale()) throw new Error('cancelled')

      media = await navigator.mediaDevices.getUserMedia({ audio: true })
      txlog('getUserMedia ok', { tracks: media.getAudioTracks().length })
      if (isStale()) throw new Error('cancelled')

      src = ctx.createMediaStreamSource(media)

      const bufferSize = 1024
      proc = ctx.createScriptProcessor(bufferSize, numberOfChannels, numberOfChannels)

      zero = ctx.createGain()
      zero.gain.value = 0

      src.connect(proc)
      proc.connect(zero)
      zero.connect(ctx.destination)
      txlog('audio graph connected', { ctxState: ctx.state, sampleRate: ctxSampleRate })

      // Re-check staleness AFTER the graph is wired but BEFORE we commit refs.
      // If stopTalking ran during the async setup above (e.g. fast tap, release
      // before getUserMedia resolved), it bumped startGenRef but couldn't stop
      // the mic tracks because txMediaStreamRef hadn't been committed yet.
      // Without this guard the freshly-built media stream gets committed AFTER
      // the user already let go — leaving a hot mic until the next press.
      if (isStale()) throw new Error('cancelled')
    } catch (err: any) {
      // Roll back any nodes/streams we created. The shared ctx must NOT be closed.
      try { proc?.disconnect() } catch (e) { NOOP(e) }
      try { zero?.disconnect() } catch (e) { NOOP(e) }
      try { src?.disconnect() } catch (e) { NOOP(e) }
      media?.getTracks?.().forEach(t => t.stop())
      if (encoder) {
        try { await encoder.close() } catch (e) { NOOP(e) }
      }
      // Tell Zello we're not actually streaming.
      safeSend({ command: 'stop_stream', seq: seqRef.current++, stream_id: streamId, channel })

      const cancelled = String(err?.message || '') === 'cancelled'
      if (!cancelled) {
        const errTxt = mediaErrorMessage(err)
        console.error('[TX] audio graph setup failed', err)
        setState(p => ({ ...p, tx: 'idle', error: errTxt, lastEvent: 'mic_error' }))
        fireError({ content: errTxt })
      } else {
        setState(p => ({ ...p, tx: 'idle' }))
      }
      return
    }

    // Commit to refs only after every step succeeded.
    txStreamIdRef.current = streamId
    txChannelRef.current = channel
    txTimestampUsRef.current = 0
    txWebCodecsRef.current = encoder
    txMediaStreamRef.current = media
    txCtxRef.current = ctx
    txSourceRef.current = src
    txProcessorRef.current = proc
    txZeroGainRef.current = zero

    const frameSamples = Math.max(1, Math.round((frameSizeMs / 1000) * ctxSampleRate))
    let ring = new Float32Array(frameSamples * 10)
    let ringLen = 0

    const AudioDataCtor = (window as any).AudioData
    if (!AudioDataCtor) {
      const errTxt = 'WebCodecs AudioData is not available in this browser.'
      console.error('[TX]', errTxt)
      // tear down what we just installed to avoid a stuck "talking" state with no transmission
      try { proc.disconnect() } catch (e) { NOOP(e) }
      try { zero?.disconnect() } catch (e) { NOOP(e) }
      try { src?.disconnect() } catch (e) { NOOP(e) }
      media?.getTracks?.().forEach(t => t.stop())
      try { await encoder?.close() } catch (e) { NOOP(e) }
      safeSend({ command: 'stop_stream', seq: seqRef.current++, stream_id: streamId, channel })
      setState(p => ({ ...p, tx: 'idle', error: errTxt, lastEvent: 'mic_error' }))
      fireError({ content: errTxt })
      return
    }

    proc.onaudioprocess = (ev) => {
      const sid = txStreamIdRef.current
      if (sid == null) return
      const wc = txWebCodecsRef.current
      if (!wc) return

      const input = ev.inputBuffer.getChannelData(0)

      if (ringLen + input.length > ring.length) {
        const keep = Math.min(ringLen, frameSamples * 5)
        ring.copyWithin(0, ringLen - keep, ringLen)
        ringLen = keep
      }
      ring.set(input, ringLen)
      ringLen += input.length

      while (ringLen >= frameSamples) {
        const pcm = ring.slice(0, frameSamples)
        ring.copyWithin(0, frameSamples, ringLen)
        ringLen -= frameSamples

        try {
          const ts = txTimestampUsRef.current
          const audioData = new AudioDataCtor({
            format: 'f32',
            sampleRate: ctxSampleRate,
            numberOfFrames: frameSamples,
            numberOfChannels,
            timestamp: ts,
            data: pcm,
          })
          txTimestampUsRef.current = ts + Math.round((frameSamples / ctxSampleRate) * 1_000_000)

          wc.encoder.encode(audioData)
          audioData.close()
        } catch (err) {
          NOOP(err)
          break
        }
      }
    }
  }, [activeTxChannelRef, authToken, forceReconnect, isLoggedOnRef, safeSend, safeSendBinary, seqRef, setState, waitForChannelReady, wsRef])

  const stopTalking = useCallback(async (channelHint?: string) => {
    // Bump the generation so any in-flight startTalking aborts instead of zombie-installing audio.
    startGenRef.current++

    const sid = txStreamIdRef.current
    // Capture the channel BEFORE we clear the ref — Zello requires `channel`
    // as a parameter on stop_stream (spec §232-244).
    const ch = txChannelRef.current || channelHint || null
    txStreamIdRef.current = null
    txChannelRef.current = null

    const enc = txWebCodecsRef.current
    txWebCodecsRef.current = null
    if (enc) {
      try { await enc.close() } catch (err) { NOOP(err) }
    }

    try { txProcessorRef.current?.disconnect() } catch (err) { NOOP(err) }
    try { txZeroGainRef.current?.disconnect() } catch (err) { NOOP(err) }
    try { txSourceRef.current?.disconnect() } catch (err) { NOOP(err) }
    txProcessorRef.current = null
    txSourceRef.current = null
    txZeroGainRef.current = null

    // The AudioContext is shared with RX playback; do NOT close it here.
    // We just clear our reference and disconnect our own nodes above.
    txCtxRef.current = null

    const media = txMediaStreamRef.current
    txMediaStreamRef.current = null
    media?.getTracks?.().forEach(t => t.stop())

    if (sid != null) {
      const payload: any = { command: 'stop_stream', seq: seqRef.current++, stream_id: sid }
      if (ch) payload.channel = ch
      safeSend(payload)
    }

    setState(p => ({ ...p, tx: 'idle' }))
  }, [safeSend, seqRef, setState])

  return {
    startTalking,
    stopTalking,
  }
}